import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

// Property map data interface — matches types/property.ts's PropertyMapData.
// (Kept here too since PropertyMap.tsx imports it from this module, mirroring
// client/'s layout.)
export interface PropertyMapData {
  id: string;
  title: string;
  address: string;
  lat: number;
  lng: number;
  price: number;
  bedrooms: number;
  bathrooms: number;
  image: string;
  rating: number | undefined;
  distanceToUniversity?: number;
}

/**
 * Trimmed from client/'s MapContext: this app has no university concept, so
 * everything university-scoped (the University type, universities list,
 * selected-university state, campus distance filtering/search) is dropped.
 * What's left is the generic map state PropertyCard/PropertyDetails' "View
 * on Map" affordance and PropertyMap.tsx actually use: where the map is
 * centered, which property is selected/hovered, and simple city-name search.
 */
interface MapContextType {
  showMap: boolean;
  setShowMap: (show: boolean) => void;
  center: { lat: number; lng: number };
  zoom: number;
  selectedProperty: string | null;
  hoveredProperty: string | null;
  setSelectedProperty: (propertyId: string | null) => void;
  setHoveredProperty: (propertyId: string | null) => void;
  flyToProperty: (lat: number, lng: number) => void;
  flyToCity: (city: string) => void;
  searchLocation: (query: string) => void;
  calculateDistance: (lat1: number, lng1: number, lat2: number, lng2: number) => number;
}

const MapContext = createContext<MapContextType | undefined>(undefined);

// Ghana city centers — used for both direct "fly to city" calls and free-text search.
const CITY_COORDINATES: Record<string, { lat: number; lng: number; zoom: number }> = {
  kumasi: { lat: 6.6500, lng: -1.6167, zoom: 12 },
  accra: { lat: 5.5600, lng: -0.2057, zoom: 11 },
  'cape coast': { lat: 5.1000, lng: -1.2500, zoom: 12 },
  tamale: { lat: 9.4000, lng: -0.8500, zoom: 12 },
  sunyani: { lat: 7.3333, lng: -2.3333, zoom: 12 },
  ho: { lat: 6.6000, lng: 0.4667, zoom: 12 },
  koforidua: { lat: 6.0833, lng: -0.2500, zoom: 12 },
  takoradi: { lat: 4.8833, lng: -1.7500, zoom: 12 },
  bolgatanga: { lat: 10.7833, lng: -0.8500, zoom: 12 },
  wa: { lat: 10.0667, lng: -2.5000, zoom: 12 },
};
const DEFAULT_CENTER = { lat: 5.5600, lng: -0.2057, zoom: 10 }; // Accra

export const MapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showMap, setShowMap] = useState(false);
  const [center, setCenter] = useState({ lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng });
  const [zoom, setZoom] = useState(DEFAULT_CENTER.zoom);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [hoveredProperty, setHoveredProperty] = useState<string | null>(null);

  const flyToProperty = useCallback((lat: number, lng: number) => {
    setCenter({ lat, lng });
    setZoom(15);
  }, []);

  const calculateDistance = useCallback((lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  const flyToCity = useCallback((city: string) => {
    const coords = CITY_COORDINATES[city.toLowerCase().trim()];
    if (coords) {
      setCenter({ lat: coords.lat, lng: coords.lng });
      setZoom(coords.zoom);
    } else {
      setCenter({ lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng });
      setZoom(DEFAULT_CENTER.zoom);
    }
  }, []);

  const searchLocation = useCallback((query: string) => {
    const searchQuery = query.toLowerCase().trim();
    for (const [city, coords] of Object.entries(CITY_COORDINATES)) {
      if (searchQuery.includes(city)) {
        setCenter({ lat: coords.lat, lng: coords.lng });
        setZoom(coords.zoom);
        return;
      }
    }
    // Default fallback
    setCenter({ lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng });
    setZoom(DEFAULT_CENTER.zoom);
  }, []);

  const value: MapContextType = useMemo(() => ({
    showMap,
    setShowMap,
    center,
    zoom,
    selectedProperty,
    hoveredProperty,
    setSelectedProperty,
    setHoveredProperty,
    flyToProperty,
    flyToCity,
    searchLocation,
    calculateDistance,
  }), [
    showMap, center, zoom, selectedProperty, hoveredProperty,
    flyToProperty, flyToCity, searchLocation, calculateDistance,
  ]);

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  );
};

export const useMap = (): MapContextType => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
};
